import React, { useCallback, useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';

// (26 Oct 2026, decision 145, part 3) The Event Administrator's Notifications bell on the
// member screen. The EA gets the same poll notices as the administrator for the events
// they run (pollsService.getPendingNotifications), each with its WhatsApp message ready
// to post to the group: "Send via WhatsApp" opens WhatsApp with the message and marks the
// notice sent; "Dismiss" clears it. It is one notice shared with the administrator, so
// whoever acts first clears it for both. Shown only while there is a notice for this
// calendar. Matches the administrator's bell in DashboardStep.jsx (wording, buttons).
//
// refreshKey: reload when it changes (MemberCalendarStep passes the event list, so a
// poll the EA has just opened or closed shows its notice at once).

export default function EventAdminBell({ calendarId, apiBaseUrl, authToken, formatDate, onStatusMessage, refreshKey }) {
  const [notices, setNotices] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const headers = { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    if (!calendarId || !authToken) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/notifications/pending`, { headers: { 'Authorization': `Bearer ${authToken}` } });
      if (!res.ok) return;
      const data = await res.json();
      setNotices((Array.isArray(data) ? data : []).filter((n) => n.calendar_id === calendarId));
    } catch (err) {
      // The bell is extra: the screen works without it.
    }
  }, [calendarId, apiBaseUrl, authToken]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (notices.length === 0) setOpen(false); }, [notices.length]);

  const showDate = (iso) => (formatDate ? formatDate(iso) : iso);
  const withDates = (text) => String(text || '').replace(/\d{4}-\d{2}-\d{2}/g, (d) => showDate(d));

  const resolve = async (notice, action) => {
    setBusyId(notice.id);
    try {
      const res = await fetch(`${apiBaseUrl}/api/notifications/${notice.id}/resolve`, {
        method: 'POST', headers, body: JSON.stringify({ action }),
      });
      if (res.ok || res.status === 404) {
        // 404: already cleared (by the administrator, or its event was deleted).
        setNotices((list) => list.filter((n) => n.id !== notice.id));
        if (res.ok && onStatusMessage) onStatusMessage(action === 'sent' ? 'Marked as sent' : 'Dismissed', 'success');
        if (res.status === 404) load();
      } else {
        const d = await res.json().catch(() => ({}));
        if (onStatusMessage) onStatusMessage(d.error || 'Failed to update notification', 'error');
      }
    } catch (err) {
      if (onStatusMessage) onStatusMessage('Failed to update notification', 'error');
    } finally {
      setBusyId(null);
    }
  };

  // As App.js handleSendPollNotification: the group sees dates in the sender's format.
  const send = (notice) => {
    window.open('https://wa.me/?text=' + encodeURIComponent(withDates(notice.message_text)), '_blank');
    resolve(notice, 'sent');
  };

  if (notices.length === 0) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:text-gray-900 text-xs whitespace-nowrap border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
        title="Pending notifications"
      >
        <Bell className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Notifications</span>
        <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
          {notices.length}
        </span>
      </button>
      {open && (
        <div className="fixed left-3 right-3 top-24 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 w-auto sm:w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
            <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
          {notices.map((n) => (
            <div key={n.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
              <p className="text-xs font-semibold text-gray-800 mb-1">
                {n.calendars?.name} — {n.events?.event_date ? showDate(n.events.event_date) : ''}
              </p>
              <p className="text-xs text-gray-600 mb-2 whitespace-pre-line">
                {withDates(n.message_text)
                  .split(/(\*[^*]+\*)/g)
                  .map((part, i) => (/^\*[^*]+\*$/.test(part)
                    ? <span key={i} className="font-semibold text-blue-600">{part.slice(1, -1)}</span>
                    : <React.Fragment key={i}>{part}</React.Fragment>))}
              </p>
              {/* (31 Oct 2026, decision 164) Advice-only notices: no "post this" note. */}
                          {!['phase1_closed_zero_offers', 'phase1_closed_event_day'].includes(n.trigger_reason) && (
                <p className="text-xs text-blue-600 mb-2">
                  Post this to the group so everyone sees it, including members who don't receive emails.
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => send(n)}
                  disabled={busyId === n.id}
                  className="flex-1 bg-green-600 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  Send via WhatsApp
                </button>
                <button
                  type="button"
                  onClick={() => resolve(n, 'skipped')}
                  disabled={busyId === n.id}
                  className="flex-1 border border-gray-300 text-gray-600 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
